import RoundedButton from "@/components/rounded-button"
import SingleReading from "@/components/single-reading"
import Combobox from "@/components/ui/combobox"
import { Icon } from "@/icons/icon"
import { createUuid } from "@/lib/utils"
import useStore from "@/store"
import { PMTruck } from "@/store/rmpm"
import { LabelValue } from "@/types/common"
import { useCallback, useMemo, useRef, useState } from "react"

export interface OnboardPMFormProps {
  trucks: PMTruck[]
  defaultValue: PMTruck[]
  onChange?: (trucks: PMTruck[]) => void
}

export default function OnboardPMForm({
  trucks,
  defaultValue,
  onChange,
}: OnboardPMFormProps) {
  const rmpmStore = useStore.use.rmpm()

  const pmProducts = useMemo<LabelValue[]>(() => {
    return rmpmStore.allProducts.pmProducts.map((product) => ({
      id: product.id,
      label: product.name,
      value: product.id,
      meta: {
        pmType: product.packagingType,
      },
    }))
  }, [rmpmStore.allProducts])
  const pmTypes = useMemo(() => {
    const pmTypeSet = new Set<string>()
    const pmTypeList: LabelValue[] = []

    for (const product of rmpmStore.allProducts.pmProducts) {
      if (!pmTypeSet.has(product.packagingType)) {
        pmTypeList.push({
          id: product.packagingType,
          label: product.packagingType,
          value: product.packagingType,
        })
      }

      pmTypeSet.add(product.packagingType)
    }

    return pmTypeList
  }, [rmpmStore.allProducts.pmProducts])
  const localTrucks = useTrucks()

  function useTrucks() {
    const [localTrucks, setLocalTrucks] = useState<PMTruck[]>(
      trucks.length
        ? trucks
        : [
          {
            id: createUuid(),
            truckNumber: "",
            products: [],
            pmType: "",
          },
        ]
    )

    function add(truck?: PMTruck) {
      let newTrucks: PMTruck[] = []
      if (truck) {
        newTrucks = [
          ...localTrucks,
          {
            ...truck,
            id: createUuid(),
          },
        ]
      } else {
        newTrucks = [
          ...localTrucks,
          {
            id: createUuid(),
            truckNumber: "",
            pmType: "",
            products: [],
          },
        ]
      }

      emitChange(newTrucks)
      setLocalTrucks(newTrucks)
    }

    function set(trucks: PMTruck[]) {
      emitChange(trucks)
      setLocalTrucks(trucks)
    }

    function update(newTruck: Partial<PMTruck>) {
      const newTrucks = localTrucks.map((truck) => {
        if (truck.id === newTruck.id) {
          return {
            ...truck,
            ...newTruck,
          }
        }

        return truck
      })

      emitChange(newTrucks)
      setLocalTrucks(newTrucks)
    }

    function remove(id: string) {
      const newTrucks = localTrucks.filter((truck) => truck.id !== id)

      emitChange(newTrucks)
      setLocalTrucks(newTrucks)
    }

    function emitChange(trucks: PMTruck[]) {
      if (onChange) {
        onChange(trucks)
      }
    }

    return {
      trucks: localTrucks,
      add,
      set,
      remove,
      update,
    }
  }

  function isDisabled(truckId: string) {
    return !!defaultValue.find((truck) => truck.id === truckId)
  }

  function handleChangeTruck(truck: PMTruck) {
    localTrucks.update(truck)
  }

  function handleAddTruck() {
    localTrucks.add()
  }

  function handleClickDelete() {
    localTrucks.remove(localTrucks.trucks.at(-1)?.id || "")
  }

  return (
    <div className="px-4 py-8">
      <h2 className="mb-6 font-semibold text-2xl">PM</h2>

      {localTrucks.trucks.map((truck, index) => (
        <div key={truck.id}>
          {index > 0 && (
            <hr className="my-4 h-0 border-t border-t-input-border w-full" />
          )}

          <TruckRow
            details={truck}
            pmTypes={pmTypes}
            allProducts={pmProducts}
            disabled={isDisabled(truck.id)}
            onChange={handleChangeTruck}
          />
        </div>
      ))}

      <div className="flex items-center justify-end gap-6 mt-6">
        {localTrucks.trucks.length > 1 && (
          <button
            className="flex gap-2 not-disabled:hover:cursor-pointer"
            type="button"
            onClick={handleClickDelete}
          >
            <Icon name="TrashCan" className="text-icon h-4 w-auto" />

            <span className="text-button text-xs font-semibold">
              Delete Entry
            </span>
          </button>
        )}

        <RoundedButton icon={<Icon name="Plus" />} onClick={handleAddTruck}>
          Truck
        </RoundedButton>
      </div>
    </div>
  )
}

interface TruckRowProps {
  details: PMTruck
  pmTypes: LabelValue[]
  allProducts: LabelValue[]
  disabled?: boolean
  onChange?: (truck: PMTruck) => void
}

function TruckRow({ details, pmTypes, allProducts, disabled, onChange }: TruckRowProps) {
  const [truckNumber, setTruckNumber] = useState(details.truckNumber || "")
  const [selectedProducts, setSelectedProducts] = useState<string[]>(details.products || [])
  const [pmType, setPmType] = useState(details.pmType || "")

  const truckNumberRef = useRef(truckNumber)
  const selectedProductsRef = useRef(selectedProducts)
  const pmTypeRef = useRef(pmType)

  const products = useMemo(() => {
    return allProducts.filter((product) => product.meta?.pmType === pmType)
  }, [allProducts, pmType])

  const emitChange = useCallback((truck: PMTruck) => {
    onChange?.(truck)
  }, [onChange])

  const handleChangeTruckNumber = useCallback((newTruckNumber: string) => {
    setTruckNumber(newTruckNumber)
    truckNumberRef.current = newTruckNumber
    emitChange({
      id: details.id,
      pmType: pmTypeRef.current,
      truckNumber: newTruckNumber,
      products: selectedProductsRef.current,
    })
  }, [details.id, emitChange])

  const handleChangePmType = useCallback((newPmType: string) => {
    setSelectedProducts([])
    setPmType(newPmType)
    selectedProductsRef.current = []
    pmTypeRef.current = newPmType
    emitChange({
      id: details.id,
      pmType: newPmType,
      truckNumber: truckNumberRef.current,
      products: [],
    })
  }, [details.id, emitChange])

  const handleChangeSelectedProducts = useCallback((newProducts: string[]) => {
    setSelectedProducts(newProducts)
    selectedProductsRef.current = newProducts
    emitChange({
      id: details.id,
      pmType: pmTypeRef.current,
      truckNumber: truckNumberRef.current,
      products: newProducts,
    })
  }, [details.id, emitChange])

  return (
    <div className="grid grid-cols-2 gap-4">
      <SingleReading
        label="Truck Number"
        direction="column"
        value={truckNumber}
        disabled={disabled}
        onChange={handleChangeTruckNumber}
      />

      <Combobox
        label="PM Type"
        options={pmTypes}
        defaultValue={[pmType]}
        multiple={false}
        disabled={disabled}
        onChange={(selected) => handleChangePmType(selected[0])}
      />

      {pmType && (
        <Combobox
          className="col-span-2"
          label="Products"
          options={products}
          multiple
          defaultValue={selectedProducts}
          disabled={disabled}
          onChange={handleChangeSelectedProducts}
        />
      )}
    </div>
  )
}
